<template>
  <ConfigBox
    :title="$t('account.settings.pix.title')"
    :callback="savePixConfig"
    :loading="accountLoading"
    @cancelEdit="resetObject(pix, paymentMethods.pix)"
    :hideEditButton="!permissionIsEnabled('settings.payments.pix:edit')"
    v-if="showPix"
  >
    <template slot="content">
      <emd-alert
        v-if="modalPix.createOrDeleteIsSuccess"
        variant="success"
        :title="modalPix.createOrDeleteAlertMessage"
        @cancel="closePixCreateOrDeleteSuccessAlert"
      />
      <div class="emd-panel">
        <div
          class="emd-panel__section"
          v-if="canEditKeyPix"
        >
          <div class="emd-panel__block">
            <div class="el-chunk">
              <h2 class="emd-text _color_default _type_caption">
                {{ $t('pix.key') }}
              </h2>
            </div>
          </div>
          <div class="emd-panel__block">
            <div class="el-chunk">
              <emd-alert
                class="eu-mt_0 eu-mb_5"
                variant="info"
                noClose
                :autoclose="false"
              >
                <template slot="content">
                  <p class="emd-text _color_info _type_caption eu-mb_1">
                    {{ $t('pix.alert.title') }}
                  </p>
                  <p class="emd-text">
                    {{ $t('pix.alert.warning') }}
                  </p>
                </template>
              </emd-alert>
              <p class="emd-text _type_caption eu-mb_2">
                {{ $t('pix.recipient') }}
              </p>
              <div class="eu-flex eu-column eu-row_md">
                <div class="eu-flex-item eu-basis_3">
                  <label
                    class="emd-label"
                    for="status"
                    >{{ $t('pix.name') }}</label
                  >
                  <span>{{ dataClientPix.name }}</span>
                </div>
                <div class="eu-flex-item eu-basis_3">
                  <label
                    class="emd-label"
                    for="status"
                    >{{ $t('pix.document') }}</label
                  >
                  <span>{{ dataClientPix.documentNumber }}</span>
                </div>
              </div>
              <div
                class="eu-display_flex eu-mt_5"
                v-if="pix.key"
              >
                <emerald-icon icon="check" />
                <p class="emd-text eu-ml_2">
                  {{ $t('pix.key-created-p1') }}
                  <strong>{{ dataClientPix.name }}</strong>
                  {{ $t('pix.key-created-p2') }}
                </p>
              </div>
              <button
                v-if="showCreateKeyButton"
                class="emd-btn _color_primary eu-my_5"
                type="button"
                @click="$emit('onToggleModalPix', 'createKeyIsOpen')"
              >
                {{ $t('pix.create-key') }}
              </button>
              <button
                v-if="showDeleteKeyButton"
                class="emd-btn _color_danger eu-my_5"
                type="button"
                @click="$emit('onToggleModalPix', 'deleteKeyIsOpen')"
              >
                {{ $t('pix.delete-key') }}
              </button>
            </div>
          </div>
        </div>

        <div class="emd-panel__section">
          <div class="emd-panel__block">
            <div class="el-chunk">
              <h2 class="emd-text _color_default _type_caption">
                {{ $t('pix.config') }}
              </h2>
            </div>
          </div>
          <div class="emd-panel__block">
            <div class="el-chunk">
              <div class="emd-form-group">
                <label
                  class="emd-label"
                  for="status"
                  >{{ $t('pix.status') }}</label
                >
                <label
                  class="emd-switcher__label"
                  for="enabledPix"
                >
                  <input
                    class="emd-switcher _color_success"
                    checked="checked"
                    type="checkbox"
                    id="enabledPix"
                    v-model="pix.enabled"
                  />
                  <div class="emd-switcher__area"></div>
                  <span class="emd-switcher__text">{{ $t('pix.active') }}</span>
                </label>
              </div>
            </div>
            <!-- Gateway pix -->
            <div class="el-chunk">
              <div class="emd-form-group">
                <label class="emd-label _required">{{
                  $t('pix.gateway')
                }}</label>
                <div
                  class="emd-select"
                  id="gatewayPix"
                >
                  <select v-model="pix.gateway">
                    <option
                      v-for="(gateway, index) in transactionalModel"
                      :value="gateway.key"
                      :key="index"
                    >
                      {{ gateway.value }}
                    </option>
                  </select>
                  <div class="emd-select__arrow"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ConfigBox>
</template>

<script>
import { mapGetters } from 'vuex'
import { EmdAlert, EmeraldIcon } from 'emerald-vue/src/components'
import ConfigBox from '@/components/project/molecules/ConfigBox.vue'
import PixConfigManager from './pix-config.manager.js'

export default {
  name: 'PixConfig',
  components: {
    ConfigBox,
    EmdAlert,
    EmeraldIcon
  },
  props: {
    pix: Object,
    pixAffiliations: Object,
    account: Object,
    merchantId: String,
    accountLoading: Boolean,
    modalPix: Object
  },
  computed: {
    ...mapGetters({
      hasCompanyIdInPagarmeSettings: 'hasCompanyIdInPagarmeSettings',
      paymentMethods: 'paymentMethods',
      businessModel: 'businessModel'
    }),
    params () {
      return {
        merchantId: this.merchantId,
        accountId: this.account.id
      }
    },
    instance () {
      return new PixConfigManager(this.pix, this.pixAffiliations)
    },
    showPix () {
      return (
        this.instance.isVisible(
          this.businessModel,
          this.hasCompanyIdInPagarmeSettings,
          this.account.account_type.value
        ) && this.permissionIsEnabled('settings.payments.pix:view')
      )
    },
    canEditKeyPix () {
      return this.instance.canEditKeyPix(
        this.account.account_type.value,
        this.dataClientPix,
        this.hasCompanyIdInPagarmeSettings,
        this.businessModel
      )
    },
    transactionalModel () {
      return this.instance.formatTransactionalModel(
        this.account.account_type.value,
        this.businessModel,
        this.hasCompanyIdInPagarmeSettings
      )
    },
    dataClientPix () {
      const data = this.instance.getDefaultPixRecipient()
      this.$emit('defaultPixRecipientEmit', data)
      this.setPixKey(data.pixKeys)
      return data
    },
    showCreateKeyButton () {
      return !this.pix.key
    },
    showDeleteKeyButton () {
      return this.pix.key
    }
  },
  methods: {
    setPixKey (key) {
      this.pix.key = key
    },
    savePixConfig (password) {
      this.pix.password = password
      return this.$store.dispatch('UPDATE_PIX_SETTINGS', this.pix)
    },
    resetObject (obj, objFromApi) {
      obj = Object.assign(obj, objFromApi)
    },
    permissionIsEnabled (action) {
      return this.$store.getters.permissionIsEnabled(action)
    },
    closePixCreateOrDeleteSuccessAlert () {
      this.modalPix.createOrDeleteIsSuccess = false
    }
  }
}
</script>

<i18n>
  {
    "pt-BR": {
      "pix": {
        "title": "Pix",
        "config": "Configurações",
        "status": "Status",
        "active": "Ativo",
        "gateway": "Modelo de negócio",
        "key": "Chave PIX",
        "recipient": "Dados da conta",
        "name": "Nome",
        "document": "CPF/CNPJ",
        "key-created-p1": "Chave aleatória de ",
        "key-created-p2": " cadastrada",
        "create-key": "Criar nova chave Pix",
        "delete-key": "Excluir chave Pix",
        "success-on-create-key": "Chave pix criada com sucesso!",
        "success-on-delete-key": "Chave pix excluída com sucesso!",
        "alert": {
          "title": "Atenção",
          "warning": "A chave aleatória é exclusiva para pagamentos por QR code"
        },
        "error-internal": "Erro ao realizar a ação, por favor tente mais tarde"
      }
    },
    "en-US": {
      "pix": {
        "title": "Pix",
        "config": "Configurations",
        "status": "Status",
        "active": "Active",
        "gateway": "Business model",
        "key": "PIX Key",
        "recipient": "Account data",
        "name": "Name",
        "document": "Document number",
        "key-created-p1": "Random key for ",
        "key-created-p2": " registered",
        "create-key": "Create new Pix key",
        "delete-key": "Delete Pix key",
        "success-on-create-key": "Pix key created successfully!",
        "success-on-delete-key": "Pix key deleted successfully!",
        "alert": {
          "title": "Attention",
          "warning": "The random key is exclusive for QR code payments"
        },
        "error-internal": "Error performing action, please try again later"
      }
    }
  }
  </i18n>
